import React from 'react';
import { motion } from 'framer-motion';
import { CharacterGallery } from './CharacterGallery';
import { FamilyLineup } from './FamilyLineup';
import { NamingContrast } from './NamingContrast';
import { LogosPathos } from './LogosPathos';
import type { BriefBlock, BriefSection, BriefVisual } from '../../types/brief';
interface BriefSectionViewProps {
  section: BriefSection;
  index: number;
  /** Phone-format typography and layout. */
  compact?: boolean;
}
export function BriefSectionView({
  section,
  index,
  compact = false
}: BriefSectionViewProps) {
  const visual = section.visual ? <div data-pdf-block data-id="element-511" data-mp="27lsleiy3fvcp">
      <SectionVisual visual={section.visual} compact={compact} data-id="element-512" data-mp="27nlsu0gj6jlr" />
    </div> : null;
  const lead = section.visualPlacement === 'lead';
  return <section data-pdf-block id={section.id} className="scroll-mt-8 border-t border-[rgba(1,51,43,0.14)] pt-8 lg:pt-10" aria-labelledby={`${section.id}-title`} data-id="element-513" data-mp="2bpqn91eszk9g">
      <motion.div initial={{
      opacity: 0,
      y: 18
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.2
    }} transition={{
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-514" data-mp="2ljz770prq4w0">
        <span className="font-body text-[10px] uppercase tracking-[0.22em] text-[rgba(1,51,43,0.4)]" data-id="element-515" data-mp="2ynv9scl0s4cf">
          {String(index + 1).padStart(2, '0')}
        </span>
        <h2 id={`${section.id}-title`} className={['mt-2 max-w-[22ch] font-heading leading-[1.06] tracking-[-0.02em] text-background', compact ? 'text-[23px]' : 'text-[32px]'].join(' ')} data-id="element-516" data-mp="2tqwkq8g7qnl3">
          {section.title}
        </h2>

        {lead && visual}

        <div className={compact ? 'mt-5 space-y-4' : 'mt-7 space-y-5'} data-id="element-517" data-mp="3hko6yjxb5pc1">
          {section.blocks.map((block, i) => <div key={`${section.id}-${i}`} data-pdf-block data-id="element-518" data-mp="dyfnnuwqx3t4" data-mpi={i}>
              <Block block={block} compact={compact} data-id="element-519" data-mp="e00htjf5ekqg" />
            </div>)}
        </div>
      </motion.div>

      {!lead && visual}
    </section>;
}
function SectionVisual({
  visual,
  compact
}: {
  visual: BriefVisual;
  compact: boolean;
}) {
  if (visual === 'characters') return <CharacterGallery compact={compact} data-id="element-520" data-mp="x1r7rutplkxl" />;
  if (visual === 'lineup') return <FamilyLineup compact={compact} data-id="element-521" data-mp="ja2uvpbmysfk" />;
  if (visual === 'pathos') return <LogosPathos compact={compact} data-id="element-522" data-mp="nchi0lmc8ecn" />;
  return <NamingContrast compact={compact} data-id="element-523" data-mp="nb6dgjl2j2kh" />;
}
function Block({
  block,
  compact
}: {
  block: BriefBlock;
  compact: boolean;
}) {
  if (block.kind === 'statement') {
    return <p className={['max-w-[24ch] py-1 font-heading leading-[1.1] tracking-[-0.02em] text-background', compact ? 'text-[22px]' : 'text-[30px]'].join(' ')} data-id="element-524" data-mp="ecb6v6e43qgn">
        {block.text}
      </p>;
  }
  if (block.kind === 'aside') {
    return <p className={['max-w-[52ch] font-body leading-relaxed text-[rgba(1,51,43,0.5)]', compact ? 'text-[12px]' : 'text-[13px]'].join(' ')} data-id="element-525" data-mp="1uprmitzu4fmp">
        {block.text}
      </p>;
  }
  if (block.kind === 'note') {
    return <p className={['font-body font-semibold leading-snug text-background', compact ? 'text-[14px]' : 'text-[16px]'].join(' ')} data-id="element-526" data-mp="3j7klx527hskw">
        {block.text}
      </p>;
  }
  if (block.kind === 'quote') {
    return <p className={['border-l-2 border-primary pl-4 font-body italic leading-relaxed text-[rgba(1,51,43,0.7)]', compact ? 'text-[14px]' : 'text-[17px] lg:pl-5'].join(' ')} data-id="element-527" data-mp="3o88g1zlqepjc">
        “{block.text}”
      </p>;
  }
  if (block.kind === 'lines') {
    return <ul className="space-y-1.5" data-id="element-528" data-mp="fsor3cs3fta3">
        {block.lines.map(line => <li key={line} className={['flex items-baseline gap-2.5 font-body leading-snug text-[rgba(1,51,43,0.85)]', compact ? 'text-[14px]' : 'text-[17px]'].join(' ')} data-id="element-529" data-mp="av6qc9o8ckd6">
            <span aria-hidden="true" className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" data-id="element-530" data-mp="2cnux2t031pxp" />
            {line}
          </li>)}
      </ul>;
  }
  return <p className={['max-w-[64ch] font-body text-[rgba(1,51,43,0.76)]', compact ? 'text-[13.5px] leading-[1.7]' : 'text-[16px] leading-[1.75]'].join(' ')} data-id="element-531" data-mp="2o13ad2rkj0y1">
      {block.text}
    </p>;
}